import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { ClipboardCheckIcon, MicroscopeIcon, ScrollTextIcon } from "lucide-react";
import { useNavigate } from "react-router";
import { ROUTES as TEST_DATA_ROUTES } from "./navigation";
import { getPatientEncounterLabel } from "./patientEncounterLabel";

const getIconForDataType = (dataType: string | null) => {
  switch (dataType) {
    case "Endoscopy":
      return (
        <TooltipProvider key={dataType}>
          <Tooltip>
            <TooltipTrigger asChild>
              <ClipboardCheckIcon className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">Endoscopy Report</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    case "Histology":
      return (
        <TooltipProvider key={dataType}>
          <Tooltip>
            <TooltipTrigger asChild>
              <MicroscopeIcon className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">Histology Report</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    case "GP Summary":
      return (
        <TooltipProvider key={dataType}>
          <Tooltip>
            <TooltipTrigger asChild>
              <ScrollTextIcon className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">GP Summary</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    default:
      return null;
  }
};

export default function PatientEncounters() {
  const navigate = useNavigate();
  // Query: fetch patient encounters with at least one medical_text_example
  const {
    data: patientEncounters,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["test-patients-with-encounters"],
    queryFn: async () => {
      const result = await supabase.from("patient_encounters").select(`
             *,
             patient:patients(*),
             patient_encounter_medical_text_examples!inner(
              *,
              medical_text_examples!inner(*)
             )
          `);
      if (result.error) throw result.error;
      return result.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  if (isLoading) {
    return <div className="p-8 text-center">Loading...</div>;
  }
  if (error) {
    return <div className="p-8 text-center text-red-500">Failed to load patient encounters.</div>;
  }
  if (!patientEncounters?.length) {
    return <div className="p-8 text-center text-muted-foreground">No patient encounters found.</div>;
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Patient</TableHead>
            <TableHead>Encounter</TableHead>
            <TableHead>Reports Available</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {patientEncounters.map((patientEncounter) => (
            <TableRow
              key={patientEncounter.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => navigate(`${TEST_DATA_ROUTES.patientEncounterDetails.slug}/${patientEncounter.id}`)}
            >
              <TableCell>{patientEncounter.patient?.id || "Unknown"}</TableCell>
              <TableCell>{getPatientEncounterLabel(patientEncounter)}</TableCell>
              <TableCell>
                <div className="flex gap-2">
                  {Array.from(
                    new Set(
                      patientEncounter.patient_encounter_medical_text_examples
                        .flatMap((i) => i.medical_text_examples)
                        .map((i) => i.test_type),
                    ),
                  )
                    .sort()
                    .map((testType) => getIconForDataType(testType))}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
